import React, { useMemo } from "react";
import Chip from "../chips/index";
import { classNames } from "components/utils";
import "../chips/chips.scss";
import { GPUInstanceType } from "types/InstanceTypes";
import { getCanonicalGpuName } from "utils/gpuNames";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";
export interface ChipType {
  name: string;
  manufacturer: string;
}

export const preferenceList: ChipType[] = [
  { name: "B300", manufacturer: "nvidia" },
  { name: "B200", manufacturer: "nvidia" },
  { name: "RTX PRO 6000", manufacturer: "nvidia" },
  { name: "RTX 5090", manufacturer: "nvidia" },
  { name: "GH200", manufacturer: "nvidia" },
  { name: "H200", manufacturer: "nvidia" },
  { name: "H100", manufacturer: "nvidia" },
  { name: "A100", manufacturer: "nvidia" },
  { name: "L40S", manufacturer: "nvidia" },
  { name: "A10", manufacturer: "nvidia" },
  { name: "A10G", manufacturer: "nvidia" },
  { name: "L4", manufacturer: "nvidia" },
  { name: "T4", manufacturer: "nvidia" },
  { name: "L40", manufacturer: "nvidia" },
  { name: "A40", manufacturer: "nvidia" },
  { name: "RTX A6000", manufacturer: "nvidia" },
  { name: "CPU", manufacturer: "" },
];

interface ToggleButtonsProps {
  allGPUInstances: GPUInstanceType[];
  selectedChip: string | null;
  setSelectedChip: (selected: string) => void;
  selectedCloud: string;
  listOfChips?: ChipType[];
  loading?: boolean;
  isLegacyPicker?: boolean;
  hideDgxc?: boolean;
  /** When set, a divider is rendered after the chip at this index (e.g. 0 to separate CPU from GPU chips) */
  dividerAfterIndex?: number;
}

const ChipToggleButtons: React.FC<ToggleButtonsProps> = ({
  allGPUInstances,
  selectedChip,
  setSelectedChip,
  selectedCloud,
  loading = false,
  listOfChips = [],
  isLegacyPicker,
  hideDgxc = false,
  dividerAfterIndex,
}) => {
  const handleSelection = (chipName: string) => {
    setSelectedChip(chipName);
  };
  const sortedChips = useMemo(() => {
    const sortedList = [...listOfChips];
    sortedList.sort((a, b) => {
      // Keep CPU first when present (don't sort it with GPU preference order)
      if (a.name === "CPU") return -1;
      if (b.name === "CPU") return 1;
      const indexA = preferenceList.findIndex(
        (preference) => preference.name === a.name,
      );
      const indexB = preferenceList.findIndex(
        (preference) => preference.name === b.name,
      );
      if (indexA === -1 && indexB === -1) {
        return 0;
      } else if (indexA === -1) {
        return 1;
      } else if (indexB === -1) {
        return -1;
      } else {
        return indexA - indexB;
      }
    });
    return sortedList;
  }, [listOfChips]);

  const chipsToRender = sortedChips.length > 0 ? sortedChips : listOfChips;

  return (
    <div
      className={classNames(
        "flex flex-row items-start overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-600 [&::-webkit-scrollbar-thumb:hover]:bg-zinc-400",
      )}
    >
      {!loading &&
        !isLegacyPicker &&
        !hideDgxc &&
        allGPUInstances.some(
          (instance) =>
            getPrimaryInstanceCloudCred(instance)?.id === "dgxc-gcp-1",
        ) && (
          <Chip
            key={"dgx-H100"}
            selected={selectedChip === "dgx-H100"}
            chipName={"H100"}
            manufacturerName={"NVIDIA"}
            onClick={() => handleSelection("dgx-H100")}
            className={classNames("h-[150px] mr-2 flex-shrink-0")}
            style={
              {
                zIndex: listOfChips.length + 1,
                opacity: loading ? 0 : 1,
                animation: loading
                  ? `stack 0.2s ease-in-out ${
                      listOfChips.length + 1 * 0.2
                    }s forwards`
                  : `fanOut 0.2s ease-in-out 0.1s forwards`,
                "--translateX": `${listOfChips.length + 1 * -115}px`,
              } as React.CSSProperties
            }
            dgx={true}
          />
        )}
      {selectedCloud !== "dgxc-gcp-1" &&
        chipsToRender.map((chip, index) => (
          <React.Fragment key={chip.name}>
            <Chip
              selected={selectedChip === chip.name}
              chipName={getCanonicalGpuName(chip.name)}
              manufacturerName={chip.manufacturer}
              onClick={() => handleSelection(chip.name)}
              className={classNames("h-[150px] mr-2 flex-shrink-0")}
              style={
                {
                  zIndex: listOfChips.length - index,
                  opacity: loading ? 0 : 1,
                  animation: loading
                    ? `stack 0.2s ease-in-out ${index * 0.2}s forwards`
                    : `fanOut 0.2s ease-in-out 0.1s forwards`,
                  "--translateX": `${index * -115}px`,
                } as React.CSSProperties
              }
            />
            {dividerAfterIndex === index && (
              <div
                className="flex-shrink-0 w-px mx-1 self-stretch bg-zinc-700 dark:bg-zinc-600 rounded-full min-h-[100px] my-4 mr-3"
                aria-hidden
              />
            )}
          </React.Fragment>
        ))}
    </div>
  );
};

export default ChipToggleButtons;
